<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import ControlCustomerPageTab from '../components/ControlCustomerPageTab.vue';
import { CUSTOMER_TAB_ROUTER_INFO } from '../constants';
import { computed, onBeforeMount } from 'vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import SelectInput from '@/corePagesModule/SelectInput';
import { CustomerForecastRepository } from '@/coreInfrastructureModule/Repositories/CustomerForecastRepository';
import { useCustomerForecastDiffLastMonthStore } from './useCustomerForecastDiffLastMonthStore';

const t = i18n.t;
const store = useCustomerForecastDiffLastMonthStore();
const { state } = storeToRefs(store);
const locale = computed(() => i18n.locale);

const customerForecastRepository = new CustomerForecastRepository();

onBeforeMount(() => {
  store.init(customerForecastRepository);
});

const monthsOptionsDisplayForecast = computed(() => {
  return store.monthsOptionsDisplayForecastNumber.map((monthsOption) => monthsOption + t('unitMonth'));
});
</script>

<template>
  <div class="customer-forecast-difference">
    <div class="customer-forecast-difference__top-area top-area">
      <ControlCustomerPageTab class="top-area__tab-bar" :tab-info-object="[...CUSTOMER_TAB_ROUTER_INFO]" />
      <div class="top-area__button-area button-area">
        <!-- TODO: モックなので、ダウンロードが必要になった段階でボタンの実装 -->
        <TextButton
          class="button-area__download-button download-button button-area__button button-area__button--white"
          :disabled="true"
          @click="() => {}"
        >
          <div class="download-button__container">
            <p class="download-button__text">{{ t('ダウンロード') }}</p>
            <SvgLoader class="download-button__icon" name="fatArrowDown" />
          </div>
        </TextButton>
      </div>
    </div>
    <div class="customer-forecast-difference__filter-content filter-content">
      <!-- 詳細フィルターモーダル -->
      <FilterModalButton
        :filter-modal-type="'customerForecast'"
        :modal-state="state.detailFilterModalState"
        :select-input-index="1"
        :filter-items="store.getFilterItemsValue().value"
        @apply="(filteredContents) => store.onAppliedDetailFilter(customerForecastRepository, filteredContents)"
      />
    </div>
    <div class="customer-forecast-difference__table-area table-area">
      <div class="table-area__top-accessory">
        <div class="table-area__select-month-area select-month-area">
          <div class="select-month-area__title">
            {{ t('forecastForReceipt_searchReceiveDate') }}
          </div>
          <MonthPicker
            class="select-month-area__calendar"
            :model-value="state.receivedForecastYearMonth"
            :locale="locale"
            :prop-format="t('MonthPickerFormat')"
            @update:model-value="store.onSelectedReceiveMonth($event, customerForecastRepository)"
          />
        </div>
        <Pagination
          v-if="state.tableContents.pagingOptions"
          :paging-options="state.tableContents.pagingOptions"
          :is-loading="state.tableContents.loadingState.status.type === 'loading'"
          class="table-area__pagination"
          @on-change-page="store.onChangePaging($event, customerForecastRepository)"
        />
        <SelectInput
          class="table-area__forecast-length-select"
          :index="21"
          :model-value="t('displayMonthsOfData', { month: state.displayForecastsMonth })"
          :placeholder="''"
          :label="''"
          :filter-type="'DISPLAY_MONTH'"
          :prop-options="monthsOptionsDisplayForecast"
          :is-open-prop="state.openedSelectInputType === 'DISPLAY_MONTH'"
          use-teleport
          @click:select-input="() => (state.openedSelectInputType = 'DISPLAY_MONTH')"
          @update:value-with-index="(_, index) => store.onSelectedDisplayForecastsMonth(index, customerForecastRepository)"
        />
      </div>
      <div
        v-if="state.tableContents.loadingState.status.type === 'zeroMatch'"
        class="table-area__business-table table-area__business-table--zero-match"
      >
        {{ t('forecastForReceipt_placeholderForZeroMatch') }}
      </div>
      <NewBaseTableContent
        v-else
        :key="state.tableContents.renderingKey"
        class="table-area__business-table table-area__business-table--success"
        :prop-table-content-properties="state.tableContents.tableHeaders"
        :prop-display-objects="store.tableContentItems"
        :prop-loading-status="state.tableContents.loadingState"
        :prop-table-type="'customerForecastDiffLastMonth'"
        @checked="(checkedDisplayIds) => store.onCheckedDisplayIds(checkedDisplayIds)"
        @on-click-sort="(sortKey) => store.sortTableContentItems(sortKey, customerForecastRepository)"
        @on-click-transition-timeline="(selectedDisplayId) => store.onClickTransitionTimelineButton(selectedDisplayId)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-forecast-difference {
  width: 100%;
  min-height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto) minmax(0, 1fr);
  gap: 8px;
  font: Noto Sans JP;
  letter-spacing: 0;
  white-space: nowrap;
}

.top-area {
  display: grid;
  grid-auto-flow: column;
  align-items: start;
  justify-content: start;
  gap: 16px;
}

.button-area {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 16px;

  &__button {
    font-weight: 700;
    border-radius: 4px;

    &--fill {
      font-size: $fontSizeS;
      background-color: $semanticPrimary;
      color: $semanticWhite;
    }

    &--white {
      font-size: $fontSizeM;
      line-height: 22px;
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
      color: $semanticPrimary;
    }
  }
}

.download-button {
  width: 156px;
  height: 34px;
  padding: 0 17px;
  display: grid;
  place-items: center;

  &__container {
    display: grid;
    grid-template-columns: auto 22px;
    place-items: center;
    gap: 4px;
  }
}

.filter-content {
  display: grid;
  grid-template-columns: auto;
  justify-content: start;

  &__select-forecast {
    width: 200px;
    height: 32px;
  }
}

.select-month-area {
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;

  &__title {
    font-size: $fontSizeS;
    line-height: 20.27px;
    font-weight: 400;
    color: $textDefault;
  }

  &__calendar {
    width: 141px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}

.table-area {
  height: 100%;
  overflow-x: auto;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  &__top-accessory {
    display: grid;
    grid-template-columns: repeat(3, auto);
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  &__business-table {
    &--zero-match {
      background-color: $surfaceDefault;
      display: grid;
      place-items: center;
      font-weight: 400;
      font-size: $fontSizeS;
      line-height: 18.2px;
      color: $textSecondary;
    }
    &--success {
      overflow-y: scroll !important;
      background-color: $backgroundDefault;
    }
    border: 1px solid $borderDefault;
    min-height: 0 !important;
    max-height: 100% !important;
  }

  &__forecast-length-select {
    width: 162px;
    height: 32px;
  }
}

.detail-filter-modal {
  position: absolute;
  width: 73%;
  z-index: 10;
}
</style>

<style lang="scss">
.select-month-area__calendar input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: $fontSizeM;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
